/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BooleanEntry, { displayStringFromFHIRBoolean } from "../datatypeComponents/BooleanEntry";
import QuantityEntry, { displayStringFromFHIRQuantity } from "../datatypeComponents/QuantityEntry";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import jsonEquals from "../functions/jsonEquals";
import StringEntry from "../datatypeComponents/StringEntry";
import ReferenceEntry, { DisplayFromFHIRReference, displayStringFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import CodeableConceptEntry, { simpleDisplayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import UriEntry, { DisplayFromFHIRUri } from "../datatypeComponents/UriEntry";
import DatatypeSelector from "../datatypeComponents/DatatypeSelector";
import MarkdownEntry from "../datatypeComponents/MarkdownEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import NarrativeEntry from "../datatypeComponents/NarrativeEntry";
import ExpandToAddOrEdit from "./ExpandToAddOrEdit";
import { sectionCodeCompetingInterests, sectionCodeConsiderations, sectionCodeEvidence, sectionCodeJudgments, sectionCodeNetEffect, sectionCodeRecommendationRating } from "../dictionaries/CodeSystemLookup";

const generateRecommendationJustificationNarrative = (resource) => {
    let status = "generated";
    let innerDiv = "";
    try {
        if (resource.title) {
            innerDiv += "<p><b>Title: </b>" + resource.title + "</p>";
        }
        innerDiv += "<p><b>Justification for Recommendation: </b>";
        if (resource.artifactReference) {
            innerDiv += displayStringFromFHIRReference(resource.artifactReference) + "</p>";
        } else if (resource.artifactCanonical) {
            innerDiv += resource.artifactCanonical + "</p>";
        } else if (resource.artifactUri) {
            innerDiv += resource.artifactUri + "</p>";
        } else {
            innerDiv += "[Reference to recommendation missing.]</p>";
        }
        if (resource.content) {
            innerDiv += "<table><tr><th>Concept</th><th>Rating</th><th>Summary</th></tr>";
            for (const content of resource.content) {
                let type = simpleDisplayStringFromFHIRCodeableConcept(content.type);
                let summary = content.summary || "";
                let rating = content.classifier?.map(classifier => simpleDisplayStringFromFHIRCodeableConcept(classifier)).join("; ") || "";
                if (summary || rating) {
                    innerDiv += "<tr><td>" + type + "</td><td>" + rating + "</td><td>" + summary + "</td></tr>";
                }
                if (content.component) {
                    for (const component of content.component) {
                        let componenttype = simpleDisplayStringFromFHIRCodeableConcept(component.type);
                        let componentsummary = component.summary || "";
                        let componentrating = component.classifier?.map(classifier => simpleDisplayStringFromFHIRCodeableConcept(classifier)).join("; ") || "";
                        if (componentsummary || componentrating) {
                            innerDiv += "<tr><td>" + componenttype + "</td><td>" + componentrating + "</td><td>" + componentsummary + "</td></tr>";
                        }
                    }
                }
            }
            innerDiv += "</table><br/>";
        }
    } catch {
        innerDiv = "[Unable to generate Narrative Summary.]"
    }
    return { "status": status, "div": '<div xmlns="http://www.w3.org/1999/xhtml">' + innerDiv + "</div>" };
}

const DisplayFromFHIRRating = ({ rating }) => {
    if (rating) {
        if (Array.isArray(rating) || typeof rating !== "object") {
            return <>[ERROR: Rating data not interpretable.]</>;
        }
        let ratingType = simpleDisplayStringFromFHIRCodeableConcept(rating.type) || "[Untyped]";
        let ratingClassifiers = "";
        if (Array.isArray(rating.classifier) && rating.classifier.length > 0) {
            ratingClassifiers = rating.classifier.map((classifier) => { return simpleDisplayStringFromFHIRCodeableConcept(classifier) }).join('; ');
        }
        let ratingQuantity = "";
        if (rating.quantity) {
            ratingQuantity = displayStringFromFHIRQuantity(rating.quantity) || "";
        }
        let ratingAuthor = "";
        if (Array.isArray(rating.author) && rating.author.length > 0) {
            ratingAuthor = rating.author.map((author) => { return displayStringFromFHIRReference(author) }).join("; ");
        }
        let ratingFreeToShare = displayStringFromFHIRBoolean(rating.freeToShare) || null;
        return <div>
            <p><b>{ratingType} ratings:</b></p>
            <div style={{ marginLeft: "24px" }}>
                {ratingClassifiers && <p>{ratingClassifiers}</p>}
                {ratingQuantity && <p>{ratingQuantity}</p>}
                {(!ratingClassifiers && !ratingQuantity) && <p>[No ratings.]</p>}
                {ratingAuthor && <p>Rated by: {ratingAuthor}</p>}
                {ratingFreeToShare === 'True' && <p>Free to share.</p>}
                {ratingFreeToShare === 'False' && <p>NOT free to share.</p>}
                {(Array.isArray(rating.component) && rating.component.length > 0) &&
                    rating.component.map((component, componentIndex) => {
                        return <div key={componentIndex}>
                            <DisplayFromFHIRRating rating={component} />
                        </div>
                    })}
            </div>
        </div>
    } else {
        return <></>
    }
}

const recommendationJustificationComponentDotAuthorResourceTypes = ['Patient', 'Practitioner', 'PractitionerRole', 'Organization', 'Device'];

const RecommendationJustificationComponentEntry = ({ elementName, fieldLabel, startingValue,
    dataEntryStyle, debug, typeValueSet, typeCCValueSet, typeSpecificValueSets,
    typeSpecificInterface, startCollapsed, foi, contentPath, containedResourceDictionary = {} }) => {

    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
    let resourceJson = newResourceDetail.revisedResourceJson;

    const [recJustComponentState, setRecJustComponentState] = useState({});
    const [typeSpecificValueSetState, setTypeSpecificValueSetState] = useState(undefined);
    const [typeSpecificInterfaceState, setTypeSpecificInterfaceState] = useState(typeSpecificInterface);
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingRecommendationJustificationComponent = {
            "summary": "", "type": "",
            "classifier": [], "quantity": "", "author": [], "path": [], "relatesTo": [], "freeToShare": true,
            "component": []
        };
        let startingTypeSpecificValueSet;
        let startingTypeSpecificInterface = typeSpecificInterface;

        if (startingValue) {
            if (startingValue.extension) { startingRecommendationJustificationComponent.extension = startingValue.extension; }
            if (startingValue.summary) { startingRecommendationJustificationComponent.summary = startingValue.summary; }
            if (startingValue.type) {
                startingRecommendationJustificationComponent.type = startingValue.type;
                if (typeSpecificValueSets) {
                    try {
                        let typeKey = startingValue.type.coding[0].code;
                        startingTypeSpecificValueSet = typeSpecificValueSets[typeKey]
                    } catch (e) { console.log(e); }
                }
                if (!typeSpecificInterface) {
                    try {
                        startingTypeSpecificInterface = startingValue.type.coding[0].code;
                    } catch (e) { console.log(e); }
                }
            }
            if (startingValue.classifier) { startingRecommendationJustificationComponent.classifier = startingValue.classifier; }
            if (startingValue.quantity) { startingRecommendationJustificationComponent.quantity = startingValue.quantity; }
            if (startingValue.author) { startingRecommendationJustificationComponent.author = startingValue.author; }
            if (startingValue.path) { startingRecommendationJustificationComponent.path = startingValue.path; }
            if (startingValue.relatedArtifact) { startingRecommendationJustificationComponent.relatesTo = startingValue.relatedArtifact; }
            if (startingValue.relatesTo) { startingRecommendationJustificationComponent.relatesTo = startingValue.relatesTo; }
            if (typeof startingValue.freeToShare === "boolean") { startingRecommendationJustificationComponent.freeToShare = startingValue.freeToShare; }
            if (startingValue.component) { startingRecommendationJustificationComponent.component = startingValue.component; }
        }
        setRecJustComponentState(JSON.parse(JSON.stringify(startingRecommendationJustificationComponent)));
        setTypeSpecificValueSetState(startingTypeSpecificValueSet);
        setTypeSpecificInterfaceState(startingTypeSpecificInterface);
    }, []);

    useEffect(() => {
        if (Object.keys(recJustComponentState).length) {
            let previousFinalValue = null;
            if (contentPath === "content[0]") {
                previousFinalValue = resourceJson.content[0].component[elementName].summary;
                resourceJson.content[0].component[elementName].summary = recJustComponentState.summary;
            } else if (contentPath === "content[1]") {
                previousFinalValue = resourceJson.content[1].component[elementName].summary;
                resourceJson.content[1].component[elementName].summary = recJustComponentState.summary;
            } else if (contentPath === "content[2]") {
                previousFinalValue = resourceJson.content[2].component[elementName].summary;
                resourceJson.content[2].component[elementName].summary = recJustComponentState.summary;
            } else if (contentPath === "content[3]") {
                previousFinalValue = resourceJson.content[3].component[elementName].summary;
                resourceJson.content[3].component[elementName].summary = recJustComponentState.summary;
            } else if (elementName === "content[4]") {
                previousFinalValue = resourceJson.content[4].summary;
                resourceJson.content[4].summary = recJustComponentState.summary;
            }
            if (recJustComponentState.summary !== undefined &&
                (previousFinalValue || recJustComponentState.summary) &&
                !jsonEquals(previousFinalValue, recJustComponentState.summary)) {
                //console.log("resourceUpdate for ArtifactAssessment.content-path.summary");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [recJustComponentState.summary]);

    useEffect(() => {
        if (Object.keys(recJustComponentState).length) {
            let previousFinalValue = null;
            if (contentPath === "content[0]") {
                previousFinalValue = resourceJson.content[0].component[elementName].quantity;
                resourceJson.content[0].component[elementName].quantity = recJustComponentState.quantity;
            } else if (contentPath === "content[1]") {
                previousFinalValue = resourceJson.content[1].component[elementName].quantity;
                resourceJson.content[1].component[elementName].quantity = recJustComponentState.quantity;
            } else if (contentPath === "content[2]") {
                previousFinalValue = resourceJson.content[2].component[elementName].quantity;
                resourceJson.content[2].component[elementName].quantity = recJustComponentState.quantity;
            } else if (contentPath === "content[3]") {
                previousFinalValue = resourceJson.content[3].component[elementName].quantity;
                resourceJson.content[3].component[elementName].quantity = recJustComponentState.quantity;
            } else if (elementName === "content[4]") {
                previousFinalValue = resourceJson.content[4].quantity;
                resourceJson.content[4].quantity = recJustComponentState.quantity;
            }

            if (recJustComponentState.quantity !== undefined &&
                (previousFinalValue || recJustComponentState.quantity) &&
                !jsonEquals(previousFinalValue, recJustComponentState.quantity)) {
                //console.log("resourceUpdate for ArtifactAssessment.content-path.quantity");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [recJustComponentState.quantity]);

    useEffect(() => {
        if (Object.keys(recJustComponentState).length) {
            if (recJustComponentState.type && Object.keys(recJustComponentState.type).length) {
                if (typeSpecificValueSets) {
                    try {
                        let typeKey = recJustComponentState.type.coding[0].code;
                        setTypeSpecificValueSetState(typeSpecificValueSets[typeKey]);
                    } catch (e) { console.log(e); }
                }
                if (!typeSpecificInterface) {
                    try {
                        setTypeSpecificInterfaceState(recJustComponentState.type.coding[0].code);
                    } catch (e) { console.log(e); }
                }
            }
            let previousFinalValue = null;
            if (contentPath === "content[0]") {
                previousFinalValue = resourceJson.content[0].component[elementName].type;
                resourceJson.content[0].component[elementName].type = recJustComponentState.type;
            } else if (contentPath === "content[1]") {
                previousFinalValue = resourceJson.content[1].component[elementName].type;
                resourceJson.content[1].component[elementName].type = recJustComponentState.type;
            } else if (contentPath === "content[2]") {
                previousFinalValue = resourceJson.content[2].component[elementName].type;
                resourceJson.content[2].component[elementName].type = recJustComponentState.type;
            } else if (contentPath === "content[3]") {
                previousFinalValue = resourceJson.content[3].component[elementName].type;
                resourceJson.content[3].component[elementName].type = recJustComponentState.type;
            } else if (elementName === "content[4]") {
                previousFinalValue = resourceJson.content[4].type;
                resourceJson.content[4].type = recJustComponentState.type;
            }
            if (recJustComponentState.type !== undefined &&
                (previousFinalValue || recJustComponentState.type) &&
                !jsonEquals(previousFinalValue, recJustComponentState.type)) {
                //console.log("resourceUpdate for ArtifactAssessment.content-path.type");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [recJustComponentState.type]);

    useEffect(() => {
        if (Object.keys(recJustComponentState).length) {
            let previousFinalValue = null;
            if (contentPath === "content[0]") {
                previousFinalValue = resourceJson.content[0].component[elementName].classifier;
                resourceJson.content[0].component[elementName].classifier = recJustComponentState.classifier;
            } else if (contentPath === "content[1]") {
                previousFinalValue = resourceJson.content[1].component[elementName].classifier;
                resourceJson.content[1].component[elementName].classifier = recJustComponentState.classifier;
            } else if (contentPath === "content[2]") {
                previousFinalValue = resourceJson.content[2].component[elementName].classifier;
                resourceJson.content[2].component[elementName].classifier = recJustComponentState.classifier;
            } else if (contentPath === "content[3]") {
                previousFinalValue = resourceJson.content[3].component[elementName].classifier;
                resourceJson.content[3].component[elementName].classifier = recJustComponentState.classifier;
            } else if (elementName === "content[4]") {
                previousFinalValue = resourceJson.content[4].classifier;
                resourceJson.content[4].classifier = recJustComponentState.classifier;
            }
            if (recJustComponentState.classifier !== undefined &&
                (previousFinalValue || recJustComponentState.classifier?.length !== 0) &&
                !jsonEquals(previousFinalValue, recJustComponentState.classifier)) {
                //console.log("resourceUpdate for ArtifactAssessment.content-path.classifier");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [recJustComponentState.classifier]);

    useEffect(() => {
        if (Object.keys(recJustComponentState).length) {
            let previousFinalValue = null;
            if (contentPath === "content[0]") {
                previousFinalValue = resourceJson.content[0].component[elementName].author;
                resourceJson.content[0].component[elementName].author = recJustComponentState.author;
            } else if (contentPath === "content[1]") {
                previousFinalValue = resourceJson.content[1].component[elementName].author;
                resourceJson.content[1].component[elementName].author = recJustComponentState.author;
            } else if (contentPath === "content[2]") {
                previousFinalValue = resourceJson.content[2].component[elementName].author;
                resourceJson.content[2].component[elementName].author = recJustComponentState.author;
            } else if (contentPath === "content[3]") {
                previousFinalValue = resourceJson.content[3].component[elementName].author;
                resourceJson.content[3].component[elementName].author = recJustComponentState.author;
            } else if (elementName === "content[4]") {
                previousFinalValue = resourceJson.content[4].author;
                resourceJson.content[4].author = recJustComponentState.author;
            }
            if (recJustComponentState.author !== undefined &&
                (previousFinalValue || recJustComponentState.author?.length !== 0) &&
                !jsonEquals(previousFinalValue, recJustComponentState.author)) {
                //console.log("resourceUpdate for ArtifactAssessment.content-path.author");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [recJustComponentState.author]);

    useEffect(() => {
        if (Object.keys(recJustComponentState).length) {
            let previousFinalValue = null;
            if (contentPath === "content[0]") {
                previousFinalValue = resourceJson.content[0].component[elementName].freeToShare;
                resourceJson.content[0].component[elementName].freeToShare = recJustComponentState.freeToShare;
            } else if (contentPath === "content[1]") {
                previousFinalValue = resourceJson.content[1].component[elementName].freeToShare;
                resourceJson.content[1].component[elementName].freeToShare = recJustComponentState.freeToShare;
            } else if (contentPath === "content[2]") {
                previousFinalValue = resourceJson.content[2].component[elementName].freeToShare;
                resourceJson.content[2].component[elementName].freeToShare = recJustComponentState.freeToShare;
            } else if (contentPath === "content[3]") {
                previousFinalValue = resourceJson.content[3].component[elementName].freeToShare;
                resourceJson.content[3].component[elementName].freeToShare = recJustComponentState.freeToShare;
            } else if (elementName === "content[4]") {
                previousFinalValue = resourceJson.content[4].freeToShare;
                resourceJson.content[4].freeToShare = recJustComponentState.freeToShare;
            }
            if (recJustComponentState.freeToShare !== undefined &&
                recJustComponentState.freeToShare !== null &&
                !jsonEquals(previousFinalValue, recJustComponentState.freeToShare)) {
                //console.log("resourceUpdate for ArtifactAssessment.content-path.freeToShare");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [recJustComponentState.freeToShare]);

    if (startCollapsedState) {
        return <>
            <div>
                <b>{fieldLabel}: </b>
                {startingValue && <DisplayFromFHIRRating rating={startingValue} />}
                &nbsp;&nbsp;
                <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
            </div>
        </>
    } else if (Object.keys(recJustComponentState).length > 0) {
        if (typeSpecificInterfaceState === 'cost') {
            return <>
                <p style={{ marginBottom: "0px" }}>
                    <b>{recJustComponentState?.type?.coding?.[0]?.display ?
                        recJustComponentState.type.coding[0].display : fieldLabel}: </b>
                </p>
                <div style={{ marginLeft: "24px" }}>
                    <CodeableConceptEntry elementName='type' fieldLabel='Type of Rating'
                        startingValue={recJustComponentState.type || null} setResourceState={setRecJustComponentState}
                        dataEntryStyle={dataEntryStyle} valueSet={typeValueSet} debug={debug}
                        codeableConceptLevelValueSet={typeCCValueSet} />
                    <br />
                    <QuantityEntry elementName='quantity' fieldLabel='Cost'
                        startingValue={recJustComponentState.quantity || null} setResourceState={setRecJustComponentState}
                        dataEntryStyle={dataEntryStyle} debug={debug} />
                    <br />
                    <MarkdownEntry elementName='summary' fieldLabel='Summary'
                        startingValue={recJustComponentState.summary || null} setResourceState={setRecJustComponentState}
                        dataEntryStyle={dataEntryStyle} debug={debug} />
                    <br />
                    <br />
                    <ArrayEntry datatype='Reference' elementName='author' fieldLabel='Rater' startCollapsed={true}
                        startingValue={recJustComponentState.author || null} setResourceState={setRecJustComponentState}
                        referencedResourceTypes={recommendationJustificationComponentDotAuthorResourceTypes} />
                    <BooleanEntry elementName="freeToShare" fieldLabel='Free to share?'
                        startingValue={recJustComponentState.freeToShare ?? true} setResourceState={setRecJustComponentState} />
                </div>
            </>
        } else {
            return <>
                <p style={{ marginBottom: "0px" }}>
                    <b>{recJustComponentState?.type?.coding?.[0]?.display ?
                        recJustComponentState.type.coding[0].display : fieldLabel}: </b>
                </p>
                <div style={{ marginLeft: "24px" }}>
                    <CodeableConceptEntry elementName='type' fieldLabel='Type of Rating'
                        startingValue={recJustComponentState.type || null} setResourceState={setRecJustComponentState}
                        dataEntryStyle={dataEntryStyle} valueSet={typeValueSet} codeableConceptLevelValueSet={typeCCValueSet} debug={debug} />
                    <br />
                    <ArrayEntry datatype="CodeableConcept" elementName='classifier' fieldLabel='Classifier Value'
                        startingValue={recJustComponentState.classifier || null} setResourceState={setRecJustComponentState}
                        dataEntryStyle={dataEntryStyle}
                        codeableConceptLevelValueSet={typeSpecificValueSetState} debug={debug} />
                    <br />
                    <MarkdownEntry elementName='summary' fieldLabel='Summary'
                        startingValue={recJustComponentState.summary || null} setResourceState={setRecJustComponentState}
                        dataEntryStyle={dataEntryStyle} debug={debug} />
                    <br />
                    <QuantityEntry elementName='quantity' fieldLabel='Quantity'
                        startingValue={recJustComponentState.quantity || null} setResourceState={setRecJustComponentState}
                        dataEntryStyle={dataEntryStyle} debug={debug} startCollapsed={true} />
                    <br />
                    <ArrayEntry datatype="RelatesTo" elementName='relatesTo' fieldLabel='Related Item'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={recJustComponentState.relatesTo || null} setResourceState={setRecJustComponentState}
                        allowedTypeValues="FHIR" doNotShowFieldLabel={true} debug={debug} startCollapsed={true}
                        startEmptyArrayClosed={true} />
                    <br />

                    <br />
                    <ArrayEntry datatype='Reference' elementName='author' fieldLabel='Rater' startCollapsed={true}
                        startingValue={recJustComponentState.author || null} setResourceState={setRecJustComponentState}
                        referencedResourceTypes={recommendationJustificationComponentDotAuthorResourceTypes} />
                    <BooleanEntry elementName="freeToShare" fieldLabel='Free to share?'
                        startingValue={recJustComponentState.freeToShare ?? true} setResourceState={setRecJustComponentState} />
                    <br />
                    <br />
                </div>
            </>
        }
    }
}

const RecommendationJustificationContentEntry = ({ elementName, fieldLabel, startingValue,
    dataEntryStyle, debug, startCollapsed, foi, containedResourceDictionary = {} }) => {

    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
    let resourceJson = newResourceDetail.revisedResourceJson;

    let typeValueSet = [
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "strength-of-recommendation", display: "Strength of Recommendation" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "summary-of-findings", display: "Summary of Findings" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "competing-interests", display: "Competing Interests" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "problem-importance", display: "Problem Importance" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "desirable-effects", display: "Desirable Effects" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "undesirable-effects", display: "Undesirable Effects" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "preferences", display: "Preferences" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "net-effect", display: "Net Effect" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "costs", display: "Costs" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "cost-effectiveness", display: "Cost-effectiveness" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "equity", display: "Equity" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "acceptability", display: "Acceptability" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "feasibility", display: "Feasibility" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "justification", display: "Justification" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "direction-of-recommendation", display: "Direction of Recommendation" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "population", display: "Population" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "action", display: "Action" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "opposite-action", display: "Opposite Action" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "subgroup-considerations", display: "Subgroup Considerations" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "implementation-considerations", display: "Implementation Considerations" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "monitoring-considerations", display: "Monitoring Considerations" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "research-considerations", display: "Research Considerations" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "rating-system", display: "Rating System" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "ratings", display: "Ratings" },
        { system: "https://fevir.net/resources/CodeSystem/179423", code: "discussion", display: "Discussion" }
    ];
    let typeCCValueSet = [
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "strength-of-recommendation", display: "Strength of Recommendation" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "summary-of-findings", display: "Summary of Findings" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "competing-interests", display: "Competing Interests" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "problem-importance", display: "Problem Importance" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "desirable-effects", display: "Desirable Effects" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "undesirable-effects", display: "Undesirable Effects" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "preferences", display: "Preferences" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "net-effect", display: "Net Effect" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "costs", display: "Costs" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "cost-effectiveness", display: "Cost-effectiveness" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "equity", display: "Equity" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "acceptability", display: "Acceptability" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "feasibility", display: "Feasibility" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "justification", display: "Justification" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "direction-of-recommendation", display: "Direction of Recommendation" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "population", display: "Population" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "action", display: "Action" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "opposite-action", display: "Opposite Action" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "subgroup-considerations", display: "Subgroup Considerations" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "implementation-considerations", display: "Implementation Considerations" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "monitoring-considerations", display: "Monitoring Considerations" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "research-considerations", display: "Research Considerations" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "rating-system", display: "Rating System" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "ratings", display: "Ratings" }] },
        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "discussion", display: "Discussion" }] }
    ];

    let typeSpecificValueSets = {
        "strength-of-recommendation": [ //Strength of Recommendation
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2001", display: "Strong expectation" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2002", display: "Weak expectation" }] }
        ],
        "summary-of-findings": [ //was Certainty of Evidence
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] }
        ],
        "competing-interests": [ //Competing Interests
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2007", display: "Yes" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2008", display: "Probably yes" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2009", display: "Probably no" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2010", display: "No" }] }
        ],
        "problem-importance": [ //Problem Importance
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2007", display: "Yes" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2008", display: "Probably yes" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2009", display: "Probably no" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2010", display: "No" }] }
        ],
        "desirable-effects": [ //Desirable Effects
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2013", display: "Large size" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2014", display: "Moderate size" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2015", display: "Small size" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2016", display: "Trivial size" }] }
        ],
        "undesirable-effects": [ //Undesirable Effects
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2013", display: "Large size" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2014", display: "Moderate size" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2015", display: "Small size" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2016", display: "Trivial size" }] }
        ],
        "preferences": [ //was Values/Preferences
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2017", display: "Important uncertainty or variability" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2018", display: "Possibly important uncertainty or variability" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2019", display: "Possibly no important uncertainty or variability" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2020", display: "No important uncertainty or variability" }] }
        ],
        "net-effect": [ //Net Effect
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2021", display: "Favors intervention" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2022", display: "Possibly favors intervention" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2023", display: "Does not favor intervention or comparison" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2024", display: "Possibly favors comparison" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2025", display: "Favors comparison" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2013", display: "Large size" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2014", display: "Moderate size" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2015", display: "Small size" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2016", display: "Trivial size" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2011", display: "Varies" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2012", display: "Unknown" }] }
        ],
        "costs": [ //Resources/Costs
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2026", display: "Large costs" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2027", display: "Moderate costs" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2028", display: "Negligible costs or savings" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2029", display: "Moderate savings" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2030", display: "Large savings" }] }
        ],
        "cost-effectiveness": [ //Cost-effectiveness
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2026", display: "Large costs" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2027", display: "Moderate costs" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2028", display: "Negligible costs or savings" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2029", display: "Moderate savings" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2030", display: "Large savings" }] }
        ],
        "equity": [ //Equity
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2007", display: "Yes" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2008", display: "Probably yes" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2009", display: "Probably no" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2010", display: "No" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] }
        ],
        "acceptability": [ //Acceptability
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2007", display: "Yes" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2008", display: "Probably yes" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2009", display: "Probably no" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2010", display: "No" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] }
        ],
        "feasibility": [ //Feasibility
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2007", display: "Yes" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2008", display: "Probably yes" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2009", display: "Probably no" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2010", display: "No" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] }
        ],
        "direction-of-recommendation": [ //Direction of Recommendation
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2021", display: "Favors intervention" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2023", display: "Does not favor intervention or comparison" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2025", display: "Favors comparison" }] }
        ]
    };
    let contentType = sectionCodeConsiderations;
    if (dataEntryStyle === "Recommendation Rating") {
        contentType = sectionCodeRecommendationRating;
        typeValueSet = [
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "strength-of-recommendation", display: "Strength of Recommendation" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "direction-of-recommendation", display: "Direction of Recommendation" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "rating-system", display: "Rating System" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "ratings", display: "Ratings" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "discussion", display: "Discussion" }
        ];
        typeCCValueSet = [
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "strength-of-recommendation", display: "Strength of Recommendation" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "direction-of-recommendation", display: "Direction of Recommendation" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "rating-system", display: "Rating System" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "ratings", display: "Ratings" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "discussion", display: "Discussion" }] }
        ];
    } else if (dataEntryStyle === "Evidence") {
        contentType = sectionCodeEvidence;
        typeValueSet = [
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "summary-of-findings", display: "Summary of Findings" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "desirable-effects", display: "Desirable Effects" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "undesirable-effects", display: "Undesirable Effects" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "discussion", display: "Discussion" }
        ];
        typeCCValueSet = [
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "summary-of-findings", display: "Summary of Findings" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "desirable-effects", display: "Desirable Effects" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "undesirable-effects", display: "Undesirable Effects" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "discussion", display: "Discussion" }] }
        ];
    } else if (dataEntryStyle === "Net Effect") {
        contentType = sectionCodeNetEffect;
        typeValueSet = [
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "preferences", display: "Preferences" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "discussion", display: "Discussion" }
        ];
        typeCCValueSet = [
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "preferences", display: "Preferences" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "discussion", display: "Discussion" }] }
        ];
    } else if (dataEntryStyle === "Judgments") {
        contentType = sectionCodeJudgments;
        typeValueSet = [
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "problem-importance", display: "Problem Importance" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "costs", display: "Costs" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "cost-effectiveness", display: "Cost-effectiveness" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "equity", display: "Equity" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "acceptability", display: "Acceptability" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "feasibility", display: "Feasibility" },
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "justification", display: "Justification" }
        ];
        typeCCValueSet = [
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "problem-importance", display: "Problem Importance" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "costs", display: "Costs" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "cost-effectiveness", display: "Cost-effectiveness" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "equity", display: "Equity" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "acceptability", display: "Acceptability" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "feasibility", display: "Feasibility" }] },
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "justification", display: "Justification" }] }
        ];
    } else if (dataEntryStyle === "Competing Interests") {
        contentType = sectionCodeCompetingInterests;
        typeValueSet = [
            { system: "https://fevir.net/resources/CodeSystem/179423", code: "competing-interests", display: "Competing Interests" },
        ];
        typeCCValueSet = [
            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "competing-interests", display: "Competing Interests" }] },
        ];
    }

    const [recommendationJustContentState, setRecommendationJustContentState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingRecommendationJustificationContent = {
            "summary": "", "type": contentType,
            "classifier": [], "quantity": "", "author": [], "path": [], "relatesTo": [], "freeToShare": true,
            "component": []
        };

        if (startingValue) {
            if (startingValue.extension) { startingRecommendationJustificationContent.extension = startingValue.extension; }
            if (startingValue.summary) { startingRecommendationJustificationContent.summary = startingValue.summary; }
            if (startingValue.type) { startingRecommendationJustificationContent.type = startingValue.type; }
            if (startingValue.classifier) { startingRecommendationJustificationContent.classifier = startingValue.classifier; }
            if (startingValue.quantity) { startingRecommendationJustificationContent.quantity = startingValue.quantity; }
            if (startingValue.author) { startingRecommendationJustificationContent.author = startingValue.author; }
            if (startingValue.path) { startingRecommendationJustificationContent.path = startingValue.path; }
            if (startingValue.relatedArtifact) { startingRecommendationJustificationContent.relatesTo = startingValue.relatedArtifact; }
            if (startingValue.relatesTo) { startingRecommendationJustificationContent.relatesTo = startingValue.relatesTo; }
            if (typeof startingValue.freeToShare === "boolean") { startingRecommendationJustificationContent.freeToShare = startingValue.freeToShare; }
            if (startingValue.component) { startingRecommendationJustificationContent.component = startingValue.component; }
        }
        setRecommendationJustContentState(JSON.parse(JSON.stringify(startingRecommendationJustificationContent)));
    }, []);

    useEffect(() => {
        if (recommendationJustContentState.type) {
            let previousFinalValue = null;
            if (elementName === "content[0]") {
                previousFinalValue = resourceJson.content[0].summary;
                resourceJson.content[0].summary = recommendationJustContentState.summary;
            } else if (elementName === "content[1]") {
                previousFinalValue = resourceJson.content[1].summary;
                resourceJson.content[1].summary = recommendationJustContentState.summary;
            } else if (elementName === "content[2]") {
                previousFinalValue = resourceJson.content[2].summary;
                resourceJson.content[2].summary = recommendationJustContentState.summary;
            } else if (elementName === "content[3]") {
                previousFinalValue = resourceJson.content[3].summary;
                resourceJson.content[3].summary = recommendationJustContentState.summary;
            }
            if (recommendationJustContentState.summary !== undefined &&
                (previousFinalValue || recommendationJustContentState.summary) &&
                !jsonEquals(previousFinalValue, recommendationJustContentState.summary)) {
                //console.log("resourceUpdate for ArtifactAssessment.content[].summary");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [recommendationJustContentState.summary]);

    useEffect(() => {
        if (recommendationJustContentState.type) {
            let previousFinalValue = null;
            if (elementName === "content[0]") {
                previousFinalValue = resourceJson.content[0].author;
                resourceJson.content[0].author = recommendationJustContentState.author;
            } else if (elementName === "content[1]") {
                previousFinalValue = resourceJson.content[1].author;
                resourceJson.content[1].author = recommendationJustContentState.author;
            } else if (elementName === "content[2]") {
                previousFinalValue = resourceJson.content[2].author;
                resourceJson.content[2].author = recommendationJustContentState.author;
            } else if (elementName === "content[3]") {
                previousFinalValue = resourceJson.content[3].author;
                resourceJson.content[3].author = recommendationJustContentState.author;
            }
            if (recommendationJustContentState.author !== undefined &&
                (previousFinalValue || recommendationJustContentState.author?.length !== 0) &&
                !jsonEquals(previousFinalValue, recommendationJustContentState.author)) {
                //console.log("resourceUpdate for ArtifactAssessment.content[].author");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [recommendationJustContentState.author]);

    useEffect(() => {
        if (recommendationJustContentState.type) {
            let previousFinalValue = null;
            if (elementName === "content[0]") {
                previousFinalValue = resourceJson.content[0].relatesTo;
                resourceJson.content[0].relatesTo = recommendationJustContentState.relatesTo;
            } else if (elementName === "content[1]") {
                previousFinalValue = resourceJson.content[1].relatesTo;
                resourceJson.content[1].relatesTo = recommendationJustContentState.relatesTo;
            } else if (elementName === "content[2]") {
                previousFinalValue = resourceJson.content[2].relatesTo;
                resourceJson.content[2].relatesTo = recommendationJustContentState.relatesTo;
            } else if (elementName === "content[3]") {
                previousFinalValue = resourceJson.content[3].relatesTo;
                resourceJson.content[3].relatesTo = recommendationJustContentState.relatesTo;
            }
            if (recommendationJustContentState.relatesTo !== undefined &&
                (previousFinalValue || recommendationJustContentState.relatesTo?.length !== 0) &&
                !jsonEquals(previousFinalValue, recommendationJustContentState.relatesTo)) {
                //console.log("resourceUpdate for ArtifactAssessment.content[].relatesTo");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [recommendationJustContentState.relatesTo]);

    useEffect(() => {
        if (recommendationJustContentState.type) {
            let previousFinalValue = null;
            if (elementName === "content[0]") {
                previousFinalValue = resourceJson.content[0].freeToShare;
                resourceJson.content[0].freeToShare = recommendationJustContentState.freeToShare;
            } else if (elementName === "content[1]") {
                previousFinalValue = resourceJson.content[1].freeToShare;
                resourceJson.content[1].freeToShare = recommendationJustContentState.freeToShare;
            } else if (elementName === "content[2]") {
                previousFinalValue = resourceJson.content[2].freeToShare;
                resourceJson.content[2].freeToShare = recommendationJustContentState.freeToShare;
            } else if (elementName === "content[3]") {
                previousFinalValue = resourceJson.content[3].freeToShare;
                resourceJson.content[3].freeToShare = recommendationJustContentState.freeToShare;
            }
            if (recommendationJustContentState.freeToShare !== undefined &&
                recommendationJustContentState.freeToShare !== null &&
                !jsonEquals(previousFinalValue, recommendationJustContentState.freeToShare)) {
                //console.log("resourceUpdate for ArtifactAssessment.content[].freeToShare");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [recommendationJustContentState.freeToShare]);

    if (startCollapsedState) {
        return <>
            <div>
                <b>{fieldLabel}: </b>
                {startingValue && <DisplayFromFHIRRating rating={startingValue} />}
                &nbsp;&nbsp;
                <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
            </div>
        </>
    } else if (Object.keys(recommendationJustContentState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <MarkdownEntry elementName='summary' fieldLabel='Summary'
                    startingValue={recommendationJustContentState.summary || null} setResourceState={setRecommendationJustContentState}
                    dataEntryStyle={dataEntryStyle} debug={debug} />
                <br />
                <ArrayEntry datatype="RelatesTo" elementName='relatesTo' fieldLabel='Related Item'
                    containedResourceDictionary={containedResourceDictionary}
                    startingValue={recommendationJustContentState.relatesTo || null} setResourceState={setRecommendationJustContentState}
                    allowedTypeValues="FHIR" doNotShowFieldLabel={true}
                    debug={debug} startCollapsed={true} startEmptyArrayClosed={true} />
                <br />
                <br />
                <ArrayEntry datatype='Reference' elementName='author' fieldLabel='Rater' startCollapsed={true}
                    startingValue={recommendationJustContentState.author || null} setResourceState={setRecommendationJustContentState}
                    referencedResourceTypes={recommendationJustificationComponentDotAuthorResourceTypes} />
                <BooleanEntry elementName="freeToShare" fieldLabel='Free to share?'
                    startingValue={recommendationJustContentState.freeToShare ?? true} setResourceState={setRecommendationJustContentState} />
                <br />
                <br />
                <ArrayEntry datatype="RecommendationJustificationComponent" elementName='component'
                    containedResourceDictionary={containedResourceDictionary}
                    fieldLabel={dataEntryStyle + " Component"} foi={foi} contentPath={elementName}
                    typeValueSet={typeValueSet} typeCCValueSet={typeCCValueSet} typeSpecificValueSets={typeSpecificValueSets}
                    startingValue={recommendationJustContentState.component || null} setResourceState={setRecommendationJustContentState}
                    dataEntryStyle={dataEntryStyle} debug={debug} />
                <br />
            </div>
        </>
    }
}

const RecommendationJustificationEdit = ({ foi }) => {

    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
    let resourceJson = newResourceDetail.revisedResourceJson;

    const [editResourceState, setEditResourceState] = useState({});
    const [resourceLoaded, setResourceLoaded] = useState(false);
    const [artifactReferenceEditState, setArtifactReferenceEditState] = useState(false);
    const [artifactReferenceDatatypeState, setArtifactReferenceDatatypeState] = useState('none');
    const [contentArrayState, setContentArrayState] = useState({});
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    useEffect(() => {
        if (resourceJson && !resourceLoaded) {
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
            let startingArtifactReferenceDatatype = 'none';
            if (resourceJson.artifactReference) { startingArtifactReferenceDatatype = 'Reference'; }
            if (resourceJson.artifactCanonical) { startingArtifactReferenceDatatype = 'canonical'; }
            if (resourceJson.artifactUri) { startingArtifactReferenceDatatype = 'uri'; }
            setArtifactReferenceDatatypeState(startingArtifactReferenceDatatype);
            if (!resourceJson.content || !Array.isArray(resourceJson.content) || resourceJson.content.length === 0) {
                resourceJson.content = [
                    {
                        "type": sectionCodeRecommendationRating
                    }
                ]
            }
            if (resourceJson.content.length === 1) {
                resourceJson.content.push({
                    "type": sectionCodeEvidence
                })
            }
            if (resourceJson.content.length === 2) {
                resourceJson.content.push({
                    "type": sectionCodeNetEffect
                })
            }
            if (resourceJson.content.length === 3) {
                resourceJson.content.push({
                    "type": sectionCodeJudgments
                })
            }
            if (resourceJson.content.length === 4) {
                resourceJson.content.push({
                    "type": sectionCodeCompetingInterests
                })
            }
            setContentArrayState({
                "content[0]": resourceJson.content[0],
                "content[1]": resourceJson.content[1],
                "content[2]": resourceJson.content[2],
                "content[3]": resourceJson.content[3],
                "content[4]": resourceJson.content[4]
            });
            setEditResourceState({
                text: resourceJson.text,
                title: resourceJson.title,
                artifactReference: resourceJson.artifactReference,
                artifactCanonical: resourceJson.artifactCanonical,
                artifactUri: resourceJson.artifactUri,
                relatesTo: resourceJson.relatesTo,
                content: resourceJson.content
            });
            setResourceLoaded(true);
        }
    }, [resourceLoaded])

    useEffect(() => {
        if (editResourceState) {
            let previousFinalValue = newResourceDetail.revisedResourceJson.title;
            if (editResourceState.title !== undefined &&
                previousFinalValue !== editResourceState.title) {
                //console.log("resourceUpdate for ArtifactAssessment.title");
                newResourceDetail.revisedResourceJson.title = editResourceState.title;
                newResourceDetail.displayTitle = editResourceState.title;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [editResourceState.title]);

    useEffect(() => {
        if (editResourceState) {
            let previousFinalValue = newResourceDetail.revisedResourceJson.text || null;
            if (editResourceState.text !== undefined &&
                (previousFinalValue || editResourceState.text) &&
                !jsonEquals(previousFinalValue, editResourceState.text)) {
                //console.log("resourceUpdate for ArtifactAssessment.text");
                newResourceDetail.revisedResourceJson.text = editResourceState.text;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [editResourceState.text]);

    useEffect(() => {
        if (editResourceState) {
            let previousFinalValue = newResourceDetail.revisedResourceJson.artifactReference || null;
            if (editResourceState.artifactReference !== undefined &&
                (previousFinalValue || editResourceState.artifactReference) &&
                !jsonEquals(previousFinalValue, editResourceState.artifactReference)) {
                //console.log("resourceUpdate for ArtifactAssessment.artifactReference");
                newResourceDetail.revisedResourceJson.artifactReference = editResourceState.artifactReference;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [editResourceState.artifactReference]);

    useEffect(() => {
        if (editResourceState) {
            let previousFinalValue = newResourceDetail.revisedResourceJson.artifactCanonical;
            if (editResourceState.artifactCanonical !== undefined &&
                (previousFinalValue || editResourceState.artifactCanonical) &&
                previousFinalValue !== editResourceState.artifactCanonical) {
                //console.log("resourceUpdate for ArtifactAssessment.artifactCanonical");
                newResourceDetail.revisedResourceJson.artifactCanonical = editResourceState.artifactCanonical;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [editResourceState.artifactCanonical]);

    useEffect(() => {
        if (editResourceState) {
            let previousFinalValue = newResourceDetail.revisedResourceJson.artifactUri || null;
            if (editResourceState.artifactUri !== undefined &&
                (previousFinalValue || editResourceState.artifactUri) &&
                previousFinalValue !== editResourceState.artifactUri) {
                //console.log("resourceUpdate for ArtifactAssessment.artifactUri");
                newResourceDetail.revisedResourceJson.artifactUri = editResourceState.artifactUri;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [editResourceState.artifactUri]);

    useEffect((() => {
        if (artifactReferenceDatatypeState === 'uri') {
            setEditResourceState(prevState => { return { ...prevState, 'artifactReference': null, 'artifactCanonical': null } })
        }
        if (artifactReferenceDatatypeState === 'Reference') {
            setEditResourceState(prevState => { return { ...prevState, 'artifactUri': null, 'artifactCanonical': null } })
        }
        if (artifactReferenceDatatypeState === 'canonical') {
            setEditResourceState(prevState => { return { ...prevState, 'artifactReference': null, 'artifactUri': null } })
        }
    }), [artifactReferenceDatatypeState]);

    return <div style={{ marginTop: "12px" }}>
        <h3 id="summary">Summary</h3>
        <div style={{ marginLeft: "24px" }}>
            <StringEntry elementName='title' fieldLabel='Title'
                startingValue={editResourceState.title}
                setResourceState={setEditResourceState} />
            <h4>Narrative Summary</h4>
            <div style={{ marginLeft: "24px" }}>
                <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                    generatedNarrative={generateRecommendationJustificationNarrative(editResourceState)}
                    startingValue={editResourceState.text} setResourceState={setEditResourceState} />
            </div>
        </div>
        <h3 id="recommendation">Recommendation</h3>
        <div>
            <div><b>The Recommendation Being Explained: </b>
                <DisplayFromFHIRReference reference={editResourceState.artifactReference}
                    containedResourceJson={editResourceState.artifactReference?.reference && containedResourceDictionary[editResourceState.artifactReference.reference]} />
                <DisplayFromFHIRUri uri={editResourceState.artifactUri || editResourceState.artifactCanonical} />
                <span className={"unselectable"} style={{ marginLeft: "24px", cursor: "pointer" }}
                    onClick={() => { setArtifactReferenceEditState(!artifactReferenceEditState) }}>
                    <i>Change the Recommendation Resource Being Explained</i>&nbsp;&nbsp;
                    {artifactReferenceEditState ? <>▼</> : <>►</>}
                </span>
            </div>
            {artifactReferenceEditState &&
                <div style={{ whiteSpace: "pre-wrap", paddingLeft: "24px" }}>
                    <DatatypeSelector elementXName='artifact[x]' allowedDatatypes={['Reference', 'canonical', 'uri']}
                        datatypeState={artifactReferenceDatatypeState} setDatatypeState={setArtifactReferenceDatatypeState} />
                    {(artifactReferenceDatatypeState === 'uri') &&
                        <UriEntry elementName='artifactUri'
                            fieldLabel='The Resource Being Rated (by URL)'
                            startingValue={editResourceState.artifactUri || null}
                            setResourceState={setEditResourceState} />}
                    {(artifactReferenceDatatypeState === 'Reference') &&
                        <ReferenceEntry elementName='artifactReference'
                            containedResourceDictionary={containedResourceDictionary}
                            fieldLabel='The Resource Being Rated'
                            startingValue={editResourceState.artifactReference || null}
                            setResourceState={setEditResourceState} />}
                    {(artifactReferenceDatatypeState === 'canonical') &&
                        <UriEntry elementName='artifactCanonical'
                            fieldLabel='The Resource Being Rated (by Canonical URI)'
                            startingValue={editResourceState.artifactCanonical || null}
                            setResourceState={setEditResourceState} />}
                    <br />
                </div>
            }
            <h3>Recommendation Rating</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[0]"] ?
                    <RecommendationJustificationContentEntry elementName='content[0]'
                        containedResourceDictionary={containedResourceDictionary}
                        fieldLabel='Recommendation Rating' foi={foi}
                        startingValue={contentArrayState["content[0]"] || null}
                        dataEntryStyle='Recommendation Rating' startCollapsed />
                    :
                    <p>Loading ...</p>}
                <br />
                <br />
            </div>
            <br />
        </div>
        <div>
            <h3 id="evidence">Evidence</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[1]"] ?
                    <RecommendationJustificationContentEntry elementName='content[1]' fieldLabel='Evidence'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={contentArrayState["content[1]"] || null} foi={foi}
                        dataEntryStyle='Evidence' startCollapsed />
                    :
                    <p>Loading ...</p>}
            </div>
            <br />
            <h3 id="net-effect">Net Effect</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[2]"] ?
                    <RecommendationJustificationContentEntry elementName='content[2]' fieldLabel='Net Effect'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={contentArrayState["content[2]"] || null} foi={foi}
                        dataEntryStyle='Net Effect' startCollapsed />
                    :
                    <p>Loading ...</p>}
            </div>
            <br />
            <h3 id="judgments">Judgments</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[3]"] ?
                    <RecommendationJustificationContentEntry elementName='content[3]' fieldLabel='Judgments'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={contentArrayState["content[3]"] || null} foi={foi}
                        dataEntryStyle='Judgments' startCollapsed />
                    :
                    <p>Loading ...</p>}
            </div>
            <br />
            <h3 id="competing-interests">Competing Interests</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[4]"] ?
                    <RecommendationJustificationComponentEntry elementName='content[4]'
                        fieldLabel='Competing Interests'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={contentArrayState["content[4]"] || null} foi={foi}
                        typeValueSet={[
                            { system: "https://fevir.net/resources/CodeSystem/179423", code: "competing-interests", display: "Competing Interests" },
                        ]} typeCCValueSet={[
                            { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "competing-interests", display: "Competing Interests" }] },
                        ]} typeSpecificValueSets={{
                            "strength-of-recommendation": [ //Strength of Recommendation
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2001", display: "Strong expectation" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2002", display: "Weak expectation" }] }
                            ],
                            "summary-of-findings": [ //was Certainty of Evidence
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] }
                            ],
                            "competing-interests": [ //Competing Interests
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2007", display: "Yes" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2008", display: "Probably yes" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2009", display: "Probably no" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2010", display: "No" }] }
                            ],
                            "problem-importance": [ //Problem Importance
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2007", display: "Yes" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2008", display: "Probably yes" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2009", display: "Probably no" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2010", display: "No" }] }
                            ],
                            "desirable-effects": [ //Desirable Effects
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2013", display: "Large size" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2014", display: "Moderate size" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2015", display: "Small size" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2016", display: "Trivial size" }] }
                            ],
                            "undesirable-effects": [ //Undesirable Effects
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2013", display: "Large size" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2014", display: "Moderate size" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2015", display: "Small size" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2016", display: "Trivial size" }] }
                            ],
                            "preferences": [ //was Values/Preferences
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2017", display: "Important uncertainty or variability" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2018", display: "Possibly important uncertainty or variability" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2019", display: "Possibly no important uncertainty or variability" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2020", display: "No important uncertainty or variability" }] }
                            ],
                            "net-effect": [ //Net Effect
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2021", display: "Favors intervention" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2022", display: "Possibly favors intervention" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2023", display: "Does not favor intervention or comparison" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2024", display: "Possibly favors comparison" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2025", display: "Favors comparison" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2013", display: "Large size" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2014", display: "Moderate size" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2015", display: "Small size" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2016", display: "Trivial size" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2011", display: "Varies" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2012", display: "Unknown" }] }
                            ],
                            "costs": [ //Resources/Costs
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2026", display: "Large costs" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2027", display: "Moderate costs" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2028", display: "Negligible costs or savings" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2029", display: "Moderate savings" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2030", display: "Large savings" }] }
                            ],
                            "cost-effectiveness": [ //Cost-effectiveness
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2026", display: "Large costs" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2027", display: "Moderate costs" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2028", display: "Negligible costs or savings" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2029", display: "Moderate savings" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2030", display: "Large savings" }] }
                            ],
                            "equity": [ //Equity
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2007", display: "Yes" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2008", display: "Probably yes" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2009", display: "Probably no" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2010", display: "No" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] }
                            ],
                            "acceptability": [ //Acceptability
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2007", display: "Yes" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2008", display: "Probably yes" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2009", display: "Probably no" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2010", display: "No" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] }
                            ],
                            "feasibility": [ //Feasibility
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2007", display: "Yes" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2008", display: "Probably yes" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2009", display: "Probably no" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2010", display: "No" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }] }
                            ],
                            "direction-of-recommendation": [ //Direction of Recommendation
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2021", display: "Favors intervention" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2023", display: "Does not favor intervention or comparison" }] },
                                { "coding": [{ system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2025", display: "Favors comparison" }] }
                            ]
                        }}
                        dataEntryStyle='Competing Interests' startCollapsed />
                    :
                    <p>Loading ...</p>}
            </div>
            <br />
        </div>
    </div>
};

export default RecommendationJustificationEdit;
export { RecommendationJustificationComponentEntry };